import { Button, useToast } from '@citizen-walk/shared-ui';
import {
  getModeLabel,
  type District,
  type DistrictMode,
  type MapDownloadPolicy,
} from '@citizen-walk/shared-types';
import { useState, type FormEvent } from 'react';
import { useModeProfiles } from '../masters';
import {
  createDistrict,
  generateUrlToken,
  updateDistrict,
  type CreateDistrictInput,
  type UpdateDistrictInput,
} from './useDistricts';

/**
 * 地区作成・編集フォーム (admin 第 10 弾、Phase 5.4 district CRUD)。
 *
 * 旧 apps/admin/src/pages/DistrictsPage.tsx (create) + DistrictDetailPage.tsx (edit) の
 * 入力 UI を踏襲した minimum 版。旧の dataSettings / aiAnalysis / mapDisplay / verification 等
 * 入れ子設定は本 sprint scope 外 (後続 admin sprint で個別 page or expandable panel 化)。
 *
 * 踏襲した点:
 *   - 「地区名 / mode / urlToken」が必須項目 (旧 line 117-140 と同構造)
 *   - 新規作成時 urlToken は自動生成 (隠し field)、編集時のみ表示+「再発行」ボタン
 *   - 投稿エリア中心 (lat/lng) と半径 (m) は generatePmtiles の前提なので編集可
 *
 * 変えた点:
 *   - Tailwind + shared-ui (Button / Modal) を使用、旧 inline style を踏襲しない
 *   - 旧の「getModeTemplate(mode) で dataSettings/userProfileOptions 初期化」を削除
 *     (Phase 5 後続 sprint で mode template 復活予定、現状は raw create で OK)
 *   - モードは「組込モード + ModeProfile 登録済み custom モード」からの select に変更し、
 *     旧 B2 の自由入力 (datalist) と「モード表示名」直接入力を撤去。新モードの追加と表示名の
 *     定義は「マスター管理 → モードプロファイル」に一本化し、地区フォームは選ぶだけにした
 *     (modeLabel は選択モードの ModeProfile.modeLabel から自動導出)。
 *   - MD-3 モードドリブン化 (2026-07-24): 新規作成は **ModeProfile 選択必須** に変更。
 *     生 mode select は撤去し、mode / modeLabel は選択した profile から自動導出する
 *     (組込4モードも seed-mode-profiles.mjs で ModeProfile 登録済みのため一覧に並ぶ)。
 *     これにより新規地区は必ず profile snapshot (dataSettings/recommendedMasters) で初期化され、
 *     @deprecated な getModeTemplate fallback (useDistricts.ts) には到達しない。
 *     編集時は mode を read-only 表示 (mode の付け替えは非対応、従来どおり)。
 */

const MAP_DOWNLOAD_POLICY_LABELS: Record<MapDownloadPolicy, string> = {
  manual: 'manual (利用者操作で DL)',
  auto: 'auto (初回入場時に促す)',
};

interface FormState {
  name: string;
  mode: DistrictMode;
  /**
   * custom モード (B2) の利用者表示名 (日本語)。フォームでは直接編集せず、選んだモードの
   * ModeProfile.modeLabel から `modeLabelFor` で自動導出する (組込モードは空)。表示名の定義は
   * 「マスター管理 → モードプロファイル」に一本化している。
   */
  modeLabel: string;
  urlToken: string;
  postAreaLat: string;
  postAreaLng: string;
  postAreaRadiusM: string;
  mapDownloadPolicy: MapDownloadPolicy;
  analysisEnabled: boolean;
  analysisDisplayConfigId: string;
  /** Sprint Y (Phase 5.5): AI 検証 opt-in flag。デフォルト false。 */
  aiVerificationEnabled: boolean;
  /** Sprint Y: AI 検証で使うアクティブ質問テンプレート ID。空文字なら未設定。 */
  aiVerificationActiveTemplateId: string;
  /** Sprint Y: 自由検証許可。デフォルト false。 */
  aiVerificationAllowFree: boolean;
  /**
   * Sprint BG-DataSettings 30: 採用 ModeProfile の id (新規作成時のみ参照、空 = 未採用 = modeTemplates fallback)。
   * 編集時は District.profileId / profileVersion を表示するためのみ使用、変更不可。
   */
  profileId: string;
}

function emptyDraft(): FormState {
  return {
    name: '',
    mode: 'ecw',
    modeLabel: '',
    urlToken: generateUrlToken(),
    postAreaLat: '',
    postAreaLng: '',
    postAreaRadiusM: '',
    mapDownloadPolicy: 'manual',
    analysisEnabled: true,
    analysisDisplayConfigId: '',
    aiVerificationEnabled: false,
    aiVerificationActiveTemplateId: '',
    aiVerificationAllowFree: false,
    profileId: '',
  };
}

function draftFromDistrict(d: District): FormState {
  return {
    name: d.name,
    mode: d.mode,
    modeLabel: d.modeLabel ?? '',
    urlToken: d.urlToken,
    postAreaLat: d.postAreaCenter ? String(d.postAreaCenter.latitude) : '',
    postAreaLng: d.postAreaCenter ? String(d.postAreaCenter.longitude) : '',
    postAreaRadiusM: typeof d.postAreaRadiusM === 'number' ? String(d.postAreaRadiusM) : '',
    mapDownloadPolicy: d.mapDownloadPolicy ?? 'manual',
    analysisEnabled: d.analysisEnabled ?? true,
    analysisDisplayConfigId: d.analysisDisplayConfigId ?? '',
    aiVerificationEnabled: d.aiVerification?.enabled ?? false,
    aiVerificationActiveTemplateId: d.aiVerification?.activeTemplateId ?? '',
    aiVerificationAllowFree: d.aiVerification?.allowFreeVerification ?? false,
    profileId: d.profileId ?? '',
  };
}

export interface DistrictEditFormProps {
  /** 編集対象。null なら新規作成モード。 */
  target: District | null;
  /** master role の uid (createdBy / updatedBy に記録)。 */
  uid: string;
  onSaved: (id: string) => void;
  onCancel: () => void;
}

export function DistrictEditForm({ target, uid, onSaved, onCancel }: DistrictEditFormProps) {
  const isNew = target === null;
  const { toast } = useToast();
  const [draft, setDraft] = useState<FormState>(() =>
    target ? draftFromDistrict(target) : emptyDraft(),
  );
  const [saving, setSaving] = useState(false);
  // MD-3 モードドリブン化: 新規作成は ModeProfile 選択必須 (mode は profile から導出)。
  // 編集時は一覧不要 (profileId は変更不可、地区側 detail panel で別途 reconcile)。
  const profilesState = useModeProfiles();
  const allProfiles = profilesState.status === 'ready' ? profilesState.items : [];

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  /**
   * MD-3: profile 選択時に mode / modeLabel を選択 profile から自動導出する。
   * mode キーの直接選択は撤去 — 登記簿 (ModeProfile) が mode の唯一の入口。
   */
  function changeProfile(profileId: string) {
    const p = allProfiles.find((x) => x.id === profileId);
    setDraft((prev) => ({
      ...prev,
      profileId,
      mode: p ? p.mode : prev.mode,
      modeLabel: p ? (p.modeLabel?.trim() ?? '') : prev.modeLabel,
    }));
  }

  function regenToken() {
    update('urlToken', generateUrlToken());
  }

  function parseAreaCenter(): { latitude: number; longitude: number } | null | undefined {
    const lat = draft.postAreaLat.trim();
    const lng = draft.postAreaLng.trim();
    if (!lat && !lng) return undefined;
    if (lat && !lng) return null;
    if (!lat && lng) return null;
    const latNum = Number(lat);
    const lngNum = Number(lng);
    if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return null;
    if (latNum < -90 || latNum > 90 || lngNum < -180 || lngNum > 180) return null;
    return { latitude: latNum, longitude: lngNum };
  }

  function parseRadius(): number | null | undefined {
    const v = draft.postAreaRadiusM.trim();
    if (!v) return undefined;
    const num = Number(v);
    if (!Number.isFinite(num) || num <= 0) return null;
    return num;
  }

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    if (!draft.name.trim()) {
      toast('error', '地区名は必須です');
      return;
    }
    const center = parseAreaCenter();
    const radius = parseRadius();
    if (center === null) {
      toast('error', '投稿エリア中心の緯度経度が不正です');
      return;
    }
    if (radius === null) {
      toast('error', '投稿エリア半径 (m) は正の数値で指定してください');
      return;
    }

    // MD-3 モードドリブン化: 新規地区は ModeProfile 必須 (@deprecated getModeTemplate fallback に
    // 到達させない)。select は required 属性付きだが、多重防御として submit 側でも検証する。
    if (isNew && !draft.profileId.trim()) {
      toast(
        'error',
        'モードプロファイルを選択してください (マスター管理 → モードプロファイル で作成できます)',
      );
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const input: CreateDistrictInput = {
          uid,
          name: draft.name,
          mode: draft.mode.trim(),
          urlToken: draft.urlToken,
          mapDownloadPolicy: draft.mapDownloadPolicy,
          analysisEnabled: draft.analysisEnabled,
          aiVerificationEnabled: draft.aiVerificationEnabled,
          aiVerificationAllowFree: draft.aiVerificationAllowFree,
        };
        if (center) input.postAreaCenter = center;
        if (typeof radius === 'number') input.postAreaRadiusM = radius;
        if (draft.modeLabel.trim()) input.modeLabel = draft.modeLabel.trim();
        if (draft.analysisDisplayConfigId.trim()) {
          input.analysisDisplayConfigId = draft.analysisDisplayConfigId.trim();
        }
        // AI 解析設定 (aiAnalysisEnabled / aiAnalysisSettingsId / aiOutputFormatIdOverride) は
        // 「AI 設定」ページ (/districts/:id/ai → DistrictAiAnalysisPanel) へ移設したため
        // create では渡さない (aiAnalysis キーは書かれず、panel 側で後から設定する)。
        // Sprint Y: activeTemplateId も同 pattern (値あり時のみ渡す)。
        const activeTemplateIdTrim = draft.aiVerificationActiveTemplateId.trim();
        if (activeTemplateIdTrim) {
          input.aiVerificationActiveTemplateId = activeTemplateIdTrim;
        }
        // Sprint BG-DataSettings 30: profileId 選択ありなら createDistrict 内部で snapshot コピー。
        const profileIdTrim = draft.profileId.trim();
        if (profileIdTrim) {
          input.profileId = profileIdTrim;
        }
        const id = await createDistrict(input);
        toast('success', `地区を作成しました (id: ${id})`);
        onSaved(id);
      } else {
        const input: UpdateDistrictInput = {
          id: target!.id,
          uid,
          name: draft.name,
          mode: draft.mode.trim(),
          modeLabel: draft.modeLabel.trim() || null,
          urlToken: draft.urlToken,
          mapDownloadPolicy: draft.mapDownloadPolicy,
          analysisEnabled: draft.analysisEnabled,
          aiVerificationEnabled: draft.aiVerificationEnabled,
          aiVerificationAllowFree: draft.aiVerificationAllowFree,
        };
        if (center !== undefined) input.postAreaCenter = center;
        if (radius !== undefined) input.postAreaRadiusM = radius;
        input.analysisDisplayConfigId = draft.analysisDisplayConfigId.trim() || null;
        // AI 解析設定 (aiAnalysisEnabled / aiAnalysisSettingsId / aiOutputFormatIdOverride) は
        // 「AI 設定」ページ (/districts/:id/ai → DistrictAiAnalysisPanel) へ移設したため
        // update では渡さない (undefined のまま → buildAiAnalysisUpdate が undefined を返し
        // payload に aiAnalysis キーを含めない = 既存値温存)。
        // Sprint Y: activeTemplateId も同 pattern (空文字 → null 明示クリア)。
        input.aiVerificationActiveTemplateId = draft.aiVerificationActiveTemplateId.trim() || null;
        await updateDistrict(input);
        toast('success', '地区を更新しました');
        onSaved(target!.id);
      }
    } catch (e) {
      toast('error', `保存に失敗しました: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-3">
      <label className="grid grid-cols-1 gap-1">
        <span className="text-xs text-slate-500">地区名 *</span>
        <input
          type="text"
          value={draft.name}
          onChange={(e) => update('name', e.target.value)}
          required
          className="min-h-touch px-3 border border-slate-300 rounded text-sm"
        />
      </label>

      {/* MD-3 モードドリブン化: 新規作成は ModeProfile 選択必須。mode / 表示名は profile から自動導出。 */}
      {isNew ? (
        <label className="grid grid-cols-1 gap-1">
          <span className="text-xs text-slate-500">モードプロファイル *</span>
          <select
            value={draft.profileId}
            onChange={(e) => changeProfile(e.target.value)}
            required
            className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
          >
            <option value="">— プロファイルを選択してください —</option>
            {allProfiles.map((p) => (
              <option key={p.id} value={p.id}>
                {getModeLabel(p.mode, p.modeLabel)} — {p.name} ({p.id})
              </option>
            ))}
          </select>
          {draft.profileId && (
            <span className="text-[11px] text-slate-500">
              モード: <code>{draft.mode}</code>
              {draft.modeLabel ? ` (${draft.modeLabel})` : ''} — 投稿フォーム項目・推奨マスタは
              このプロファイルから地区へコピーされます。
            </span>
          )}
          {profilesState.status === 'loading' && (
            <span className="text-[11px] text-slate-400">プロファイル一覧を読込中…</span>
          )}
          {profilesState.status === 'ready' && allProfiles.length === 0 && (
            <span className="text-[11px] text-danger-600">
              プロファイルが未登録です。先に「マスター管理 →
              モードプロファイル」で作成してください。
            </span>
          )}
          <span className="text-[11px] text-slate-400">
            新しいモード (調査タイプ) を追加するには、先に「マスター管理 → モードプロファイル」で
            モードと表示名を登録してください (ここには登録済みプロファイルだけが並びます)。
          </span>
        </label>
      ) : (
        <div className="grid grid-cols-1 gap-1">
          <span className="text-xs text-slate-500">モード (変更不可)</span>
          <span className="text-sm text-slate-700">
            {getModeLabel(draft.mode, draft.modeLabel)}{' '}
            <code className="text-xs">({draft.mode})</code>
          </span>
          {target?.profileId && (
            <span className="text-xs text-slate-500">
              採用 profile: <code>{target.profileId}</code>
              {target.profileVersion ? ` (version: ${target.profileVersion})` : ''}
            </span>
          )}
        </div>
      )}

      <label className="grid grid-cols-1 gap-1">
        <span className="text-xs text-slate-500">
          urlToken{' '}
          <span className="text-slate-400">
            (32-byte hex、利用者の参加 URL `/m/:urlToken` に使用)
          </span>
        </span>
        <div className="flex gap-2">
          <input
            type="text"
            value={draft.urlToken}
            onChange={(e) => update('urlToken', e.target.value)}
            className="flex-1 min-h-touch px-3 border border-slate-300 rounded text-xs font-mono"
          />
          <Button type="button" variant="secondary" size="sm" onClick={regenToken}>
            再生成
          </Button>
        </div>
      </label>

      <div className="grid grid-cols-2 gap-2">
        <label className="grid grid-cols-1 gap-1">
          <span className="text-xs text-slate-500">投稿エリア中心 緯度</span>
          <input
            type="text"
            inputMode="decimal"
            placeholder="例: 35.66"
            value={draft.postAreaLat}
            onChange={(e) => update('postAreaLat', e.target.value)}
            className="min-h-touch px-3 border border-slate-300 rounded text-sm"
          />
        </label>
        <label className="grid grid-cols-1 gap-1">
          <span className="text-xs text-slate-500">投稿エリア中心 経度</span>
          <input
            type="text"
            inputMode="decimal"
            placeholder="例: 139.70"
            value={draft.postAreaLng}
            onChange={(e) => update('postAreaLng', e.target.value)}
            className="min-h-touch px-3 border border-slate-300 rounded text-sm"
          />
        </label>
      </div>
      <p className="text-[11px] text-slate-400 -mt-2">
        ※ PMTiles 生成 (`generatePmtiles` callable) に必須。両方空欄なら未設定として扱う。
      </p>

      <label className="grid grid-cols-1 gap-1">
        <span className="text-xs text-slate-500">投稿エリア半径 (m)</span>
        <input
          type="text"
          inputMode="numeric"
          placeholder="例: 1500"
          value={draft.postAreaRadiusM}
          onChange={(e) => update('postAreaRadiusM', e.target.value)}
          className="min-h-touch px-3 border border-slate-300 rounded text-sm"
        />
      </label>

      <label className="grid grid-cols-1 gap-1">
        <span className="text-xs text-slate-500">マップダウンロード方針</span>
        <select
          value={draft.mapDownloadPolicy}
          onChange={(e) => update('mapDownloadPolicy', e.target.value as MapDownloadPolicy)}
          className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
        >
          {(Object.keys(MAP_DOWNLOAD_POLICY_LABELS) as MapDownloadPolicy[]).map((p) => (
            <option key={p} value={p}>
              {MAP_DOWNLOAD_POLICY_LABELS[p]}
            </option>
          ))}
        </select>
      </label>

      <label className="grid grid-cols-1 gap-1">
        <span className="text-xs text-slate-500">分析アプリ (Drawer link 表示)</span>
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.analysisEnabled}
            onChange={(e) => update('analysisEnabled', e.target.checked)}
            className="min-w-[1rem] min-h-[1rem]"
          />
          <span>有効 (`/m/:urlToken/analysis` を Drawer に表示)</span>
        </label>
      </label>

      <label className="grid grid-cols-1 gap-1">
        <span className="text-xs text-slate-500">
          M5 analysisDisplayConfigId{' '}
          <span className="text-slate-400">(空なら canonical default を使用)</span>
        </span>
        <input
          type="text"
          value={draft.analysisDisplayConfigId}
          onChange={(e) => update('analysisDisplayConfigId', e.target.value)}
          placeholder="例: ecw-pilot-display"
          className="min-h-touch px-3 border border-slate-300 rounded text-sm font-mono"
        />
      </label>

      {/* AI 解析設定 (aiAnalysisEnabled / settingsId / outputFormatIdOverride) は
          「AI 設定」ページ (/districts/:id/ai → DistrictAiAnalysisPanel) へ移設した。
          編集フォームでは扱わない (移設先で M1 マスター select から地区別に設定する)。 */}
      <p className="text-[11px] text-slate-400 border border-slate-100 rounded-md p-2 bg-slate-50">
        AI 解析設定 (有効化 / 使用する AI 解析設定) は「AI 設定」ページ (作成後に{' '}
        <code>地区 → AI 設定</code>) へ移動しました。
      </p>

      {/* Sprint Y (Phase 5.5): AI 検証設定 — user 側 useDistrictVerificationConfig が参照、
          allowFreeVerification && activeTemplateId 有りで「自由検証」セクションを user に表示。 */}
      <fieldset className="grid grid-cols-1 gap-2 border border-slate-200 rounded-md p-3">
        <legend className="text-xs font-bold text-slate-600">AI 検証設定</legend>
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.aiVerificationEnabled}
            onChange={(e) => update('aiVerificationEnabled', e.target.checked)}
            className="min-w-[1rem] min-h-[1rem]"
          />
          <span>AI 検証を有効化</span>
        </label>
        <label className="grid grid-cols-1 gap-1">
          <span className="text-xs text-slate-500">
            アクティブテンプレート ID (M9 verificationTemplatePresets または地区別
            aiVerificationTemplates 参照)
          </span>
          <input
            type="text"
            value={draft.aiVerificationActiveTemplateId}
            onChange={(e) => update('aiVerificationActiveTemplateId', e.target.value)}
            placeholder="例: ecw-default-verification"
            className="min-h-touch px-3 border border-slate-300 rounded text-sm font-mono"
          />
        </label>
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.aiVerificationAllowFree}
            onChange={(e) => update('aiVerificationAllowFree', e.target.checked)}
            className="min-w-[1rem] min-h-[1rem]"
          />
          <span>自由検証を許可 (admin が指定した投稿以外への検証も許可)</span>
        </label>
      </fieldset>

      <div className="flex justify-end gap-2 mt-2">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
          キャンセル
        </Button>
        <Button type="submit" disabled={saving}>
          {saving ? '保存中…' : isNew ? '作成' : '更新'}
        </Button>
      </div>
    </form>
  );
}
